import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserPlus,
  FileText,
  TrendingUp,
  RefreshCw,
  CheckCircle,
  XCircle,
  Filter,
  Info,
  Activity,
  BarChart3,
  Target,
  Award,
  Star,
  Briefcase,
  Clock,
  AlertCircle,
  Cake,
  PartyPopper,
  Download,
  ChevronRight,
  Zap,
  Eye,
  ChevronLeft,
  Calendar,
  Percent
} from 'lucide-react';

const Dashboard = ({
  candidates,
  currentUser,
  dataLoading,
  insightsData,
  buFilter,
  setBuFilter,
  uniqueBUHeads,
  setSelectedReasonModal
}) => {
  
const [apiMetrics, setApiMetrics] = useState(null);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [selectedYears, setSelectedYears] = useState([]);
const [selectedMonths, setSelectedMonths] = useState([]);
const [showYearDropdown, setShowYearDropdown] = useState(false);
const [showMonthDropdown, setShowMonthDropdown] = useState(false);
const [availableMonths, setAvailableMonths] = useState([]);
const [isLoadingMonths, setIsLoadingMonths] = useState(true);
const [activitiesData, setActivitiesData] = useState(null);
const [activitiesLoading, setActivitiesLoading] = useState(true);


// Build filter parameters for API calls
const getFilterParams = () => {
  const years = selectedYears.length > 0 ? selectedYears.join(',') : 'all';
  const months = selectedMonths.length > 0 ? selectedMonths.join(',') : 'all';
  const bus = buFilter !== 'all' ? buFilter : 'all';
  
  return { years, months, bus };
};

// Fetch available months from database
const fetchAvailableMonths = async () => {
  setIsLoadingMonths(true);
  try {
    const response = await fetch(
      'https://candi.vdpl.co/Candidates/Candidates/get_available_months.php'
    );
    const result = await response.json();
    
    console.log('🔍 Available Months Response:', result);
    
    if (result.success && result.data) {
      setAvailableMonths(result.data);
      console.log('✅ Available Months Loaded:', result.data.length, 'months');
    } else {
      console.error('Failed to fetch available months:', result.message);
      setAvailableMonths([]);
    }
  } catch (error) {
    console.error('Error fetching available months:', error);
    setAvailableMonths([]);
  } finally {
    setIsLoadingMonths(false);
  }
};


  const fetchKPIData = async () => {
  setApiLoading(true);
  setApiError(null);
  
  try {
    const filters = getFilterParams();
    const response = await fetch(
      `https://candi.vdpl.co/Candidates/Candidates/get_kpi_data.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch KPI data');
    }
    
    const result = await response.json();
    
    if (result.success) {
      setApiMetrics(result.data);
    } else {
      throw new Error(result.message || 'Failed to load KPI data');
    }
  } catch (error) {
    console.error('Error fetching KPI data:', error);
    setApiError(error.message);
  } finally {
    setApiLoading(false);
  }
};


  const fetchActivitiesData = async () => {
  setActivitiesLoading(true);
  
  try {
    const filters = getFilterParams();
    const response = await fetch(
      `https://candi.vdpl.co/Candidates/Candidates/get_dashboard_activities.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch activities data');
    }
    
    const result = await response.json();
    
    if (result.success) {
      setActivitiesData(result.data);
    } else {
      throw new Error(result.message || 'Failed to load activities data');
    }
  } catch (error) {
    console.error('Error fetching activities data:', error);
  } finally {
    setActivitiesLoading(false);
  }
};

  // Initial load
useEffect(() => {
  fetchAvailableMonths();
  fetchKPIData();
  fetchActivitiesData();
}, []);

// Auto-refresh when filters change
useEffect(() => {
  fetchKPIData();
  fetchActivitiesData();
}, [selectedYears, selectedMonths, buFilter]);


  // Close dropdowns when clicking outside
useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.relative')) {
      setShowYearDropdown(false);
      setShowMonthDropdown(false);
    }
  };
  
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

  const getFilteredCandidatesForDashboard = () => {
  if (!candidates) return [];
  return candidates.filter(candidate => {
    if (!candidate) return false;
    const isReal = Number(candidate.isRealCandidate ?? candidate.is_real_candidate) === 1;
    const matchesBU = buFilter === 'all' || candidate.buHead === buFilter;
    return isReal && matchesBU;
  });
};

  const filteredDashboardCandidates = getFilteredCandidatesForDashboard();

  // Celebration carousel state
  const [currentCelebrationSlide, setCurrentCelebrationSlide] = useState(0);
  const celebrationSlides = [
    { type: 'birthdays', reason: 'UPCOMING_BIRTHDAYS' },
    { type: 'anniversaries', reason: 'UPCOMING_ANNIVERSARIES' }
  ];

  // Data collection carousel state
  const [currentDataSlide, setCurrentDataSlide] = useState(0);
  const dataSlides = [
    { type: 'reporting', name: 'Reporting Info' },
    { type: 'project', name: 'Project Details' },
    { type: 'feedback', name: 'Client Feedback' }
  ];

  // Auto-slide effect for celebrations
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCelebrationSlide((prev) => (prev + 1) % celebrationSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Auto-slide effect for data collection
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDataSlide((prev) => (prev + 1) % dataSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentCelebrationSlide((prev) => (prev + 1) % celebrationSlides.length);
  };

  const prevSlide = () => {
    setCurrentCelebrationSlide((prev) => (prev - 1 + celebrationSlides.length) % celebrationSlides.length);
  };

  // Calculate metrics
  const calculateMetrics = () => {
    // Use API data if available, otherwise fallback to local calculation
    if (apiMetrics && !apiLoading) {
      return {
        overallHeadcount: apiMetrics.overallHeadcount,
        newJoiners: apiMetrics.newJoiners,
        avgExtRate: apiMetrics.avgExtRate,
        overallEngagement: apiMetrics.overallEngagement,
        upcomingRollOffs: apiMetrics.upcomingRollOffs,
        candidates10Plus: apiMetrics.experienceDistribution.exp10Plus,
        candidates5Plus: apiMetrics.experienceDistribution.exp5Plus,
        candidates2Plus: apiMetrics.experienceDistribution.exp2Plus,
        candidates0To1: apiMetrics.experienceDistribution.exp0To1,
        extensionInitiated: apiMetrics.extensionStatus.extensionInitiated,
        contractsConfirmed: apiMetrics.extensionStatus.contractsConfirmed,
        pendingConfirmations: apiMetrics.extensionStatus.pendingConfirmations,
        upcomingBirthdays: apiMetrics.celebrations.upcomingBirthdays,
        upcomingAnniversaries: apiMetrics.celebrations.upcomingAnniversaries,
        monthlyTrend: apiMetrics.monthlyTrend,
        reportingPending: apiMetrics.dataCollection.reporting.pending,
        extensionDuesTotal: apiMetrics.upcomingRollOffs,
        extensionConnected: 0, // Not in API, keep local
        extensionNotConnected: 0, // Not in API, keep local
        redeployment: apiMetrics.additionalMetrics.redeployment,
        totalActiveCandidates: apiMetrics.overallHeadcount,
        uniqueClients: apiMetrics.additionalMetrics.uniqueClients
      };
    }

    const overallHeadcount = filteredDashboardCandidates.filter(c => c?.status === 'Active').length;
    
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const newJoiners = filteredDashboardCandidates.filter(c => {
      if (!c || !c.startDate) return false;
      const startDate = new Date(c.startDate);
      return startDate >= thirtyDaysAgo && c.status === 'Active';
    }).length;
    
    const reportingPending = filteredDashboardCandidates.filter(c => {
      if (!c || c.status !== 'Active') return false;
      const hasReportingInfo = (c.workEmail && c.workEmail.trim()) ||
        (c.reportingManagerName && c.reportingManagerName.trim()) ||
        (c.reportingManagerEmail && c.reportingManagerEmail.trim());
      return !hasReportingInfo;
    }).length;
    
    const today = new Date();
    const sixtyDaysFromNow = new Date();
    sixtyDaysFromNow.setDate(today.getDate() + 60);
    
    const extensionDues = filteredDashboardCandidates.filter(c => {
      if (!c || !c.sowEndDate || c.status !== 'Active') return false;
      const endDate = new Date(c.sowEndDate);
      return endDate <= sixtyDaysFromNow && endDate >= today;
    });
    
    const extensionConnected = extensionDues.filter(c => 
      c.extensionStatus === 'Yes' || c.extensionRequestedByClient === 'Yes'
    ).length;
    
    const extensionNotConnected = extensionDues.filter(c => 
      c.extensionStatus !== 'Yes' && c.extensionRequestedByClient !== 'Yes'
    ).length;
    
    const redeployment = filteredDashboardCandidates.filter(c => 
      c?.status === 'Inactive'
    ).length;
    
    const totalActiveCandidates = overallHeadcount;
    
    const uniqueClients = new Set(
      filteredDashboardCandidates
        .filter(c => c?.status === 'Active' && c?.client)
        .map(c => c.client)
    ).size;
    
    const rollingOffCandidates = filteredDashboardCandidates.filter(c => {
      if (!c || !c.sowEndDate || c.status !== 'Active') return false;
      const endDate = new Date(c.sowEndDate);
      return endDate <= sixtyDaysFromNow && endDate >= today;
    });
    
    const extendedCandidates = rollingOffCandidates.filter(c => 
      c.extensionStatus === 'Yes'
    ).length;
    
    const totalActiveCandidatesWithExt = filteredDashboardCandidates.filter(c => 
      c?.status === 'Active' && c?.extensionStatus === 'Yes'
    ).length;
    
    const totalActiveCount = filteredDashboardCandidates.filter(c => c?.status === 'Active').length;
    
    const avgExtRate = totalActiveCount > 0 
      ? ((totalActiveCandidatesWithExt / totalActiveCount) * 100).toFixed(1)
      : 0;
    
    const activeCandidates = filteredDashboardCandidates.filter(c => c?.status === 'Active');
    
    const clientFeedbackCompleted = activeCandidates.filter(c => 
      c.clientRating && c.clientFeedback && 
      c.clientRating !== '0' && 
      typeof c.clientFeedback === 'string' && 
      c.clientFeedback.trim() !== ''
    ).length;
    
    const projectDetailsCompleted = activeCandidates.filter(c => 
      (c.project_tech_stack && c.project_tech_stack.trim()) ||
      (c.project_key_technologies && c.project_key_technologies.trim()) ||
      (c.project_business_problem && c.project_business_problem.trim()) ||
      (c.project_scope_objectives && c.project_scope_objectives.trim()) ||
      (c.project_role_responsibility && c.project_role_responsibility.trim()) ||
      (c.project_experience_rating && c.project_experience_rating > 0)
    ).length;
    
    const reportingInfoCompleted = activeCandidates.filter(c => 
      (c.workEmail && c.workEmail.trim()) ||
      (c.reportingManagerName && c.reportingManagerName.trim()) ||
      (c.reportingManagerEmail && c.reportingManagerEmail.trim())
    ).length;
    
    const totalPossibleEntries = activeCandidates.length * 3;
    const totalCompletedEntries = clientFeedbackCompleted + projectDetailsCompleted + reportingInfoCompleted;
    const overallEngagement = totalPossibleEntries > 0 
      ? ((totalCompletedEntries / totalPossibleEntries) * 100).toFixed(1)
      : 0;
    
    const calculateExperience = (candidate) => {
  if (!candidate || !candidate.startDate) return 0;
  
  const startDate = new Date(candidate.startDate);
  const today = new Date();
  
  if (isNaN(startDate.getTime())) return 0;
  
  // Calculate years of experience
  let years = today.getFullYear() - startDate.getFullYear();
  const monthDiff = today.getMonth() - startDate.getMonth();
  
  // Adjust if birthday hasn't occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < startDate.getDate())) {
    years--;
  }
  
  return years;
};

const activeCandidatesForExp = filteredDashboardCandidates.filter(c => c?.status === 'Active');

const candidates10Plus = activeCandidatesForExp.filter(c => 
  calculateExperience(c) >= 10
).length;

const candidates5Plus = activeCandidatesForExp.filter(c => 
  calculateExperience(c) >= 5 && calculateExperience(c) < 10
).length;

const candidates2Plus = activeCandidatesForExp.filter(c => 
  calculateExperience(c) >= 2 && calculateExperience(c) < 5
).length;

const candidates0To1 = activeCandidatesForExp.filter(c => 
  calculateExperience(c) >= 0 && calculateExperience(c) < 2
).length;
    
    const extensionInitiated = filteredDashboardCandidates.filter(c => 
      c?.status === 'Active' && c?.extensionRequestedByClient === 'Yes'
    ).length;
    
    const contractsConfirmed = filteredDashboardCandidates.filter(c => 
      c?.status === 'Active' && c?.extensionStatus === 'Yes'
    ).length;
    
    const pendingConfirmations = filteredDashboardCandidates.filter(c => 
      c?.status === 'Active' && 
      c?.extensionRequestedByClient === 'Yes' && 
      c?.extensionStatus !== 'Yes'
    ).length;
    
    const thirtyDaysAhead = new Date();
    thirtyDaysAhead.setDate(today.getDate() + 30);
    
    const isWithinNext30Days = (dateString) => {
      if (!dateString) return false;
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return false;
        
        const currentYear = today.getFullYear();
        const eventDate = new Date(currentYear, date.getMonth(), date.getDate());
        
        if (eventDate < today) {
          eventDate.setFullYear(currentYear + 1);
        }
        
        return eventDate >= today && eventDate <= thirtyDaysAhead;
      } catch {
        return false;
      }
    };
    
    const upcomingBirthdays = filteredDashboardCandidates.filter(c => 
      c?.status === 'Active' && c?.dob && isWithinNext30Days(c.dob)
    ).length;
    
    const upcomingAnniversaries = filteredDashboardCandidates.filter(c => 
      c?.status === 'Active' && c?.startDate && isWithinNext30Days(c.startDate)
    ).length;
    
    return {
      overallHeadcount,
      newJoiners,
      reportingPending,
      extensionDuesTotal: extensionDues.length,
      extensionConnected,
      extensionNotConnected,
      redeployment,
      totalActiveCandidates,
      uniqueClients,
      avgExtRate,
      overallEngagement,
      candidates10Plus,
      candidates5Plus,
      candidates2Plus,
      candidates0To1,
      extensionInitiated,
      contractsConfirmed,
      pendingConfirmations,
      upcomingBirthdays,
      upcomingAnniversaries
    };
  };

  const metrics = calculateMetrics();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [animatedMetrics, setAnimatedMetrics] = useState({});

  // Animated counter effect
  useEffect(() => {
    const animateValue = (key, end) => {
      const duration = 1000;
      const start = animatedMetrics[key] || 0;
      const increment = (end - start) / (duration / 16);
      let current = start;
      
      const timer = setInterval(() => {
        current += increment;
        if ((increment > 0 && current >= end) || (increment < 0 && current <= end)) {
          current = end;
          clearInterval(timer);
        }
        setAnimatedMetrics(prev => ({ ...prev, [key]: Math.round(current) }));
      }, 16);
    };

    Object.keys(metrics).forEach(key => {
      if (typeof metrics[key] === 'number' && !key.includes('Rate') && !key.includes('Engagement')) {
        animateValue(key, metrics[key]);
      }
    });
  }, [metrics.overallHeadcount, metrics.newJoiners]);

  const handleRefresh = () => {
  setIsRefreshing(true);
  fetchAvailableMonths();
  fetchKPIData();
  fetchActivitiesData();
  setTimeout(() => setIsRefreshing(false), 1500);
};

  if (!insightsData || apiLoading) return (
    <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#0047bb] border-t-transparent mb-4"></div>
        <p className="text-gray-600 font-medium">
          {apiLoading ? 'Loading KPI data...' : 'Loading dashboard...'}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
    {/* Power BI Style Top Bar */}
<div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
  <div className="w-full px-6 py-2.5">
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center space-x-4">
        <h1 className="text-base lg:text-lg font-semibold text-[#0047bb] truncate">
          Portfolio Management Dashboard
        </h1>
      </div>
      
      {/* Right Section - Filters and Actions */}
      <div className="flex items-center space-x-2 flex-wrap gap-2">
        
        {/* Year Multi-Select Filter */}
        <div className="relative">
          <button
            onClick={() => {
              setShowYearDropdown(!showYearDropdown);
              setShowMonthDropdown(false);
            }}
            className="flex items-center space-x-2 bg-white rounded-lg px-3 py-1.5 border border-gray-200 shadow-sm hover:shadow-md transition-all"
          >
            <Calendar className="w-4 h-4 text-[#0047bb]" />
            <span className="text-sm font-semibold text-gray-900">
              Years {selectedYears.length > 0 && `(${selectedYears.length})`}
            </span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showYearDropdown ? 'rotate-90' : ''}`} />
          </button>
          
          {showYearDropdown && (
            <div className="absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 min-w-[200px] max-h-[300px] overflow-y-auto">
              <div className="p-2">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 mb-2">
                  <span className="text-xs font-bold text-gray-700">Select Years</span>
                  <button
                    onClick={() => setSelectedYears([])}
                    className="text-xs text-[#0047bb] hover:underline font-semibold"
                  >
                    Clear All
                  </button>
                </div>
                {!isLoadingMonths && availableMonths.length > 0 && (() => {
                  const uniqueYears = [...new Set(availableMonths.map(m => m.year))].sort((a, b) => b - a);
                  return uniqueYears.map(year => (
                    <label key={year} className="flex items-center space-x-3 px-3 py-2 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedYears.includes(year)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedYears([...selectedYears, year]);
                          } else {
                            setSelectedYears(selectedYears.filter(y => y !== year));
                          }
                        }}
                        className="w-4 h-4 text-[#0047bb] border-gray-300 rounded focus:ring-[#0047bb]"
                      />
                      <span className="text-sm text-gray-900 font-medium">{year}</span>
                    </label>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Month Multi-Select Filter */}
        <div className="relative">
          <button
            onClick={() => {
              setShowMonthDropdown(!showMonthDropdown);
              setShowYearDropdown(false);
            }}
            className="flex items-center space-x-2 bg-white rounded-lg px-3 py-1.5 border border-gray-200 shadow-sm hover:shadow-md transition-all"
          >
            <Calendar className="w-4 h-4 text-[#0047bb]" />
            <span className="text-sm font-semibold text-gray-900">
              Months {selectedMonths.length > 0 && `(${selectedMonths.length})`}
            </span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showMonthDropdown ? 'rotate-90' : ''}`} />
          </button>
          
          {showMonthDropdown && (
            <div className="absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 min-w-[200px] max-h-[300px] overflow-y-auto">
              <div className="p-2">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 mb-2">
                  <span className="text-xs font-bold text-gray-700">Select Months</span>
                  <button
                    onClick={() => setSelectedMonths([])}
                    className="text-xs text-[#0047bb] hover:underline font-semibold"
                  >
                    Clear All
                  </button>
                </div>
                {!isLoadingMonths && availableMonths.length > 0 && (() => {
                  const monthNames = {
                    '01': 'January', '02': 'February', '03': 'March',
                    '04': 'April', '05': 'May', '06': 'June',
                    '07': 'July', '08': 'August', '09': 'September',
                    '10': 'October', '11': 'November', '12': 'December'
                  };
                  
                  const uniqueMonths = availableMonths.map(m => ({
                    value: m.value,
                    display: `${monthNames[m.month]} ${m.year}`
                  }));
                  
                  return uniqueMonths.map(month => (
                    <label key={month.value} className="flex items-center space-x-3 px-3 py-2 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedMonths.includes(month.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMonths([...selectedMonths, month.value]);
                          } else {
                            setSelectedMonths(selectedMonths.filter(m => m !== month.value));
                          }
                        }}
                        className="w-4 h-4 text-[#0047bb] border-gray-300 rounded focus:ring-[#0047bb]"
                      />
                      <span className="text-sm text-gray-900 font-medium">{month.display}</span>
                    </label>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        <button className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1.5">
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Export</span>
        </button>
        
        <button 
          onClick={handleRefresh}
          className="px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-[#0047bb] to-[#0066ff] rounded hover:from-[#003a99] hover:to-[#0052dd] transition-colors flex items-center space-x-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>
    </div>
  </div>
</div>

      {/* Main Dashboard Content */}
      <div className="w-full px-6 lg:px-8 py-4">
        
        {/* Top Row - KPI Cards + Data Collection */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-3">
          
          {/* Left + Center: 4 KPI Cards */}
          <div className="lg:col-span-12">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {/* Overall Active Headcount */}
              <div 
                className="bg-white rounded border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'ALL_ACTIVE', bu: buFilter })}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="w-8 h-8 rounded bg-gradient-to-br from-[#0047bb] to-[#0066ff] flex items-center justify-center">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
                <div className="text-2xl font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent mb-0.5">
                  {animatedMetrics.overallHeadcount || metrics.overallHeadcount}
                </div>
                <div className="text-xs font-medium text-gray-600">Overall Active HC</div>
              </div>

              {/* Newly Onboarded */}
              <div 
                className="bg-white rounded border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'NEW_JOINERS', bu: buFilter })}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="w-8 h-8 rounded bg-[#10C1A8] flex items-center justify-center">
                    <UserPlus className="w-4 h-4 text-white" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
                <div className="text-2xl font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent mb-0.5">
                  {animatedMetrics.newJoiners || metrics.newJoiners}
                </div>
                <div className="text-xs font-medium text-gray-600">Newly Onboarded (30d)</div>
              </div>

              {/* Avg Extension Rate */}
              <div className="bg-white rounded border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="w-8 h-8 rounded bg-green-500 flex items-center justify-center">
                    <Percent className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="text-2xl font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent mb-0.5">
                  {metrics.avgExtRate}%
                </div>
                <div className="text-xs font-medium text-gray-600">Avg Extension Rate</div>
              </div>

              {/* Overall Engagement */}
              <div className="bg-white rounded border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="w-8 h-8 rounded bg-blue-500 flex items-center justify-center">
                    <Activity className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="text-2xl font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent mb-0.5">
                  {metrics.overallEngagement}%
                </div>
                <div className="text-xs font-medium text-gray-600">Overall Engagement</div>
              </div>
              
              {/* NEW 5th KPI Card - Upcoming Roll Offs */}
<div 
  className="bg-white rounded border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
  onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'UPCOMING_ROLLOFFS', bu: buFilter })}
>
  <div className="flex items-start justify-between mb-2">
    <div className="w-8 h-8 rounded bg-orange-500 flex items-center justify-center">
      <Calendar className="w-4 h-4 text-white" />
    </div>
    <ChevronRight className="w-4 h-4 text-gray-400" />
  </div>
  <div className="text-2xl font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent mb-0.5">
    {(() => {
      const today = new Date();
      const sixtyDaysFromNow = new Date();
      sixtyDaysFromNow.setDate(today.getDate() + 60);
      
      return filteredDashboardCandidates.filter(c => {
        if (!c || !c.sowEndDate || c.status !== 'Active') return false;
        const endDate = new Date(c.sowEndDate);
        return endDate <= sixtyDaysFromNow && endDate >= today;
      }).length;
    })()}
  </div>
  <div className="text-xs font-medium text-gray-600">Upcoming Roll Offs (60d)</div>
</div>

            </div>
          </div>

          
        </div>

        {/* Middle Section - Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-3">
          
          {/* Left Column - Celebrations & Quick Stats */}
          <div className="lg:col-span-2 space-y-3 flex flex-col">
            
            {/* Celebrations Carousel */}
            <div className="bg-white rounded border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <PartyPopper className="w-4 h-4 text-[#0047bb]" />
                  <h3 className="text-sm font-semibold text-[#0047bb]">Celebrations</h3>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={prevSlide}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="relative overflow-hidden">
                <div 
                  className="flex transition-transform duration-500"
                  style={{ transform: `translateX(-${currentCelebrationSlide * 100}%)` }}
                >
                  {/* Birthdays */}
                  <div className="w-full flex-shrink-0 pr-2">
                    <div 
                      className="bg-gradient-to-br from-blue-50 to-blue-100 rounded p-4 cursor-pointer hover:shadow-md transition-shadow border border-blue-200"
                      onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'UPCOMING_BIRTHDAYS', bu: buFilter })}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded bg-gradient-to-br from-[#0047bb] to-[#0066ff] flex items-center justify-center">
                          <Cake className="w-5 h-5 text-white" />
                        </div>
                        <span className="px-2 py-0.5 bg-white/80 text-[#0047bb] text-[10px] font-semibold rounded">
                          30 DAYS
                        </span>
                      </div>
                      <div className="text-3xl font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent mb-1">
                        {animatedMetrics.upcomingBirthdays || metrics.upcomingBirthdays}
                      </div>
                      <div className="text-xs font-medium text-gray-700">Upcoming Birthdays</div>
                    </div>
                  </div>

                  {/* Anniversaries */}
                  <div className="w-full flex-shrink-0 pl-2">
                    <div 
                      className="bg-gradient-to-br from-green-50 to-green-100 rounded p-4 cursor-pointer hover:shadow-md transition-shadow border border-green-200"
                      onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'UPCOMING_ANNIVERSARIES', bu: buFilter })}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded bg-[#10C1A8] flex items-center justify-center">
                          <Award className="w-5 h-5 text-white" />
                        </div>
                        <span className="px-2 py-0.5 bg-white/80 text-[#10C1A8] text-[10px] font-semibold rounded">
                          30 DAYS
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-[#10C1A8] mb-1">
                        {animatedMetrics.upcomingAnniversaries || metrics.upcomingAnniversaries}
                      </div>
                      <div className="text-xs font-medium text-gray-700">Work Anniversaries</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-1.5 mt-3">
                {celebrationSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentCelebrationSlide(index)}
                    className={`h-1.5 rounded-full transition-all ${
                      index === currentCelebrationSlide 
                        ? 'bg-[#003A64] w-4' 
                        : 'bg-gray-300 w-1.5'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Experience Distribution */}
            <div className="bg-white rounded border border-gray-200 p-4">
              <div className="flex items-center space-x-2 mb-3 bg-gradient-to-r from-amber-100 to-transparent px-3 py-1.5 rounded-lg w-fit">
                <Star className="w-4 h-4 text-[#0047bb]" />
                <h3 className="text-sm font-semibold text-[#0047bb]">Experience Distribution</h3>
              </div>
              <div className="space-y-3">
                <div 
                  className="flex items-center justify-between p-2.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'EXP_10_PLUS', bu: buFilter })}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#0047bb] to-[#0066ff]"></div>
                    <span className="text-sm text-gray-700">10+ Years</span>
                  </div>
                  <span className="text-sm font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent">
                    {animatedMetrics.candidates10Plus || metrics.candidates10Plus}
                  </span>
                </div>
                <div 
                  className="flex items-center justify-between p-2.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'EXP_5_PLUS', bu: buFilter })}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#10C1A8]"></div>
                    <span className="text-sm text-gray-700">5-10 Years</span>
                    </div>
                  <span className="text-sm font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent">
                    {animatedMetrics.candidates5Plus || metrics.candidates5Plus}
                  </span>
                </div>
                <div 
                  className="flex items-center justify-between p-2.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'EXP_2_PLUS', bu: buFilter })}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-sm text-gray-700">2-5 Years</span>
                  </div>
                  <span className="text-sm font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent">
                    {animatedMetrics.candidates2Plus || metrics.candidates2Plus}
                  </span>
                </div>
                <div 
              className="flex items-center justify-between p-2.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'EXP_0_TO_1', bu: buFilter })}
            >
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-700">0-2 Years</span>
              </div>
              <span className="text-sm font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent">
                {animatedMetrics.candidates0To1 || metrics.candidates0To1}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Center Column - Action Items Table */}
      <div className="lg:col-span-7 flex flex-col gap-3">
        <div className="bg-white rounded border border-gray-200 overflow-hidden flex flex-col">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h2 className="text-base font-semibold text-[#0047bb]">Immediate Actions Required</h2>
              </div>
              <div className="flex items-center space-x-2 px-2.5 py-1 bg-white rounded border border-red-200">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-xs font-semibold text-red-600">Action Items</span>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto overflow-y-auto powerbi-scrollbar" style={{ maxHeight: '240px' }}>
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    Candidate
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    Client
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    Action Items
                  </th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    Priority
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(() => {
                  const actionItems = [];
                  
                  filteredDashboardCandidates
                    .filter(c => c?.status === 'Active')
                    .forEach(candidate => {
                      const missingItems = [];
                      
                      const hasReportingInfo = (candidate.workEmail && candidate.workEmail.trim()) ||
                        (candidate.reportingManagerName && candidate.reportingManagerName.trim()) ||
                        (candidate.reportingManagerEmail && candidate.reportingManagerEmail.trim());
                      if (!hasReportingInfo) {
                        missingItems.push('reporting');
                      }
                      
                      const hasProjectDetails = (candidate.project_tech_stack?.trim() || 
                        candidate.project_key_technologies?.trim() || 
                        candidate.project_business_problem?.trim() || 
                        candidate.project_scope_objectives?.trim() || 
                        candidate.project_role_responsibility?.trim() || 
                        (candidate.project_experience_rating && candidate.project_experience_rating > 0));
                      if (!hasProjectDetails) {
                        missingItems.push('project');
                      }
                      
                      const hasClientFeedback = candidate.clientRating && 
                        candidate.clientFeedback && 
                        candidate.clientRating !== '0' && 
                        typeof candidate.clientFeedback === 'string' && 
                        candidate.clientFeedback.trim() !== '';
                      if (!hasClientFeedback) {
                        missingItems.push('feedback');
                      }
                      
                      if (missingItems.length > 0) {
                        actionItems.push({
                          candidate,
                          missingItems,
                          priority: missingItems.length
                        });
                      }
                    });
                  
                  actionItems.sort((a, b) => b.priority - a.priority);
                  
                  if (actionItems.length === 0) {
                    return (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center">
                          <div className="flex flex-col items-center space-y-2">
                            <CheckCircle className="w-12 h-12 text-[#10C1A8]" />
                            <p className="text-sm font-semibold text-gray-600">All candidates have complete information</p>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  
                  return actionItems.slice(0, 50).map((item, idx) => (
                    <tr 
                      key={idx}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded bg-gradient-to-br from-[#0047bb] to-[#0066ff] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
  {(item.candidate.candidate_name || item.candidate.name || item.candidate.candidateName || '?').charAt(0).toUpperCase()}
</div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
  {item.candidate.candidate_name || item.candidate.name || item.candidate.candidateName || 'Unknown'}
</div>
<div className="text-xs text-gray-500 truncate">
  {item.candidate.job_title || item.candidate.jobTitle || item.candidate.designation || 'N/A'}
</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700 truncate">
                          {item.candidate.client || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {(() => {
                            const hasReporting = (item.candidate.workEmail?.trim() || 
                              item.candidate.reportingManagerName?.trim() || 
                              item.candidate.reportingManagerEmail?.trim());
                            
                            return hasReporting ? (
                              <span className="inline-flex items-center justify-center p-1.5 rounded bg-green-100 border border-green-200 group relative">
                                <CheckCircle className="w-4 h-4 text-green-700" />
                                <FileText className="w-3 h-3 text-green-700 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-0.5 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                  Reporting Info ✓
                                </span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center p-1.5 rounded bg-gray-100 border border-gray-200 group relative">
                                <XCircle className="w-4 h-4 text-gray-600" />
                                <FileText className="w-3 h-3 text-gray-600 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-0.5 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                  Reporting Info ✗
                                </span>
                              </span>
                            );
                          })()}

                          {(() => {
                            const hasProject = (item.candidate.project_tech_stack?.trim() || 
                              item.candidate.project_key_technologies?.trim() || 
                              item.candidate.project_business_problem?.trim() || 
                              item.candidate.project_scope_objectives?.trim() || 
                              item.candidate.project_role_responsibility?.trim() || 
                              (item.candidate.project_experience_rating && item.candidate.project_experience_rating > 0));
                            
                            return hasProject ? (
                              <span className="inline-flex items-center justify-center p-1.5 rounded bg-green-100 border border-green-200 group relative">
                                <CheckCircle className="w-4 h-4 text-green-700" />
                                <Briefcase className="w-3 h-3 text-green-700 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-0.5 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                  Project Details ✓
                                </span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center p-1.5 rounded bg-gray-100 border border-gray-200 group relative">
                                <XCircle className="w-4 h-4 text-gray-600" />
                                <Briefcase className="w-3 h-3 text-gray-600 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-0.5 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                  Project Details ✗
                                </span>
                              </span>
                            );
                          })()}

                          {(() => {
                            const hasFeedback = item.candidate.clientRating && 
                              item.candidate.clientFeedback && 
                              item.candidate.clientRating !== '0' && 
                              item.candidate.clientRating !== 0 &&
                              typeof item.candidate.clientFeedback === 'string' && 
                              item.candidate.clientFeedback.trim() !== '';
                            
                            return hasFeedback ? (
                              <span className="inline-flex items-center justify-center p-1.5 rounded bg-green-100 border border-green-200 group relative">
                                <CheckCircle className="w-4 h-4 text-green-700" />
                                <Star className="w-3 h-3 text-green-700 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-0.5 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                  Client Feedback ✓
                                </span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center p-1.5 rounded bg-gray-100 border border-gray-200 group relative">
                                <XCircle className="w-4 h-4 text-gray-600" />
                                <Star className="w-3 h-3 text-gray-600 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-0.5 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                  Client Feedback ✗
                                </span>
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.priority === 3 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-semibold border border-red-200">
                            High
                          </span>
                        ) : item.priority === 2 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-xs font-semibold border border-orange-200">
                            Medium
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 text-xs font-semibold border border-yellow-200">
                            Low
                          </span>
                        )}
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
          
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <div className="flex items-center space-x-2">
                <Info className="w-4 h-4 text-[#0047bb]" />
                <span>
                  <span className="font-semibold text-[#0047bb]">
                    {(() => {
                      let count = 0;
                      filteredDashboardCandidates
                        .filter(c => c?.status === 'Active')
                        .forEach(c => {
                          const hasReporting = (c.workEmail?.trim() || c.reportingManagerName?.trim() || c.reportingManagerEmail?.trim());
                          const hasProject = (c.project_tech_stack?.trim() || c.project_key_technologies?.trim() || c.project_business_problem?.trim() || c.project_scope_objectives?.trim() || c.project_role_responsibility?.trim() || (c.project_experience_rating && c.project_experience_rating > 0));
                          const hasFeedback = c.clientRating && c.clientFeedback && c.clientRating !== '0' && typeof c.clientFeedback === 'string' && c.clientFeedback.trim() !== '';
                          if (!hasReporting || !hasProject || !hasFeedback) count++;
                        });
                      return count;
                    })()} candidates
                  </span> require attention
                </span>
              </div>
              <span>Showing top 50 items</span>
            </div>
          </div>
        </div>

        {/* Extension Status and Timeline - Below Action Items */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          
          {/* Extension Metrics */}
          <div className="lg:col-span-2 bg-white rounded border border-gray-200 p-4">
            <div className="flex items-center space-x-2 mb-3 bg-gradient-to-r from-blue-100 to-transparent px-3 py-1.5 rounded-lg w-fit">
              <Clock className="w-4 h-4 text-[#0047bb]" />
              <h3 className="text-sm font-semibold text-[#0047bb]">Extension Status</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
              <div className="space-y-3">
                <div 
                  className="flex items-center justify-between p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200"
                  onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'EXTENSION_INITIATED', bu: buFilter })}
                >
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4 text-[#0047bb]" />
                    <span className="text-sm font-medium text-gray-700">Requested by AM</span>
                  </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent">
                    {animatedMetrics.extensionInitiated || metrics.extensionInitiated}
                  </span>
                </div>
                <div 
                  className="flex items-center justify-between p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100 transition-colors border border-green-200"
                  onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'CONTRACTS_CONFIRMED', bu: buFilter })}
                >
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-[#10C1A8]" />
                    <span className="text-sm font-medium text-gray-700">Confirmed</span>
                  </div>
                  <span className="text-xl font-bold text-[#10C1A8]">
                    {animatedMetrics.contractsConfirmed || metrics.contractsConfirmed}
                  </span>
                </div>
                <div 
                  className="flex items-center justify-between p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100 transition-colors border border-orange-200"
                  onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'PENDING_CONFIRMATIONS', bu: buFilter })}
                >
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-gray-700">Pending</span>
                  </div>
                  <span className="text-xl font-bold text-orange-600">
                    {animatedMetrics.pendingConfirmations || metrics.pendingConfirmations}
                  </span>
                </div>
              </div>

              <div className="flex flex-col">
                <div className="flex items-center space-x-2 mb-3">
                  <BarChart3 className="w-4 h-4 text-[#0047bb]" />
                  <h3 className="text-sm font-semibold text-[#0047bb]">Monthly Trend</h3>
                </div>
                
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="flex items-center space-x-1.5">
                    <div className="w-3 h-3 rounded bg-gradient-to-r from-[#0047bb] to-[#0066ff]"></div>
                    <span className="text-xs font-medium text-gray-700">Initiated</span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <div className="w-3 h-3 rounded bg-[#10C1A8]"></div>
                    <span className="text-xs font-medium text-gray-700">Confirmed</span>
                  </div>
                </div>

                <div className="flex-grow flex items-end justify-between gap-2 px-2 min-h-[140px]">
                  {(() => {
                    const monthlyData = metrics.monthlyTrend || [
  { month: 'Jan', initiated: 0, confirmed: 0 },
  { month: 'Feb', initiated: 0, confirmed: 0 },
  { month: 'Mar', initiated: 0, confirmed: 0 },
  { month: 'Apr', initiated: 0, confirmed: 0 },
  { month: 'May', initiated: 0, confirmed: 0 },
  { month: 'Jun', initiated: 0, confirmed: 0 }
];
                    const maxValue = Math.max(...monthlyData.map(d => Math.max(d.initiated, d.confirmed)), 1);

                    return monthlyData.map((data, idx) => (
  <div key={data.month} className="flex-1 flex flex-col items-center h-full">
    <div className="w-full flex items-end justify-center gap-1 flex-grow">
      <div 
        className="flex-1 bg-gradient-to-r from-[#0047bb] to-[#0066ff] rounded-t cursor-pointer hover:opacity-80 transition-opacity"
        style={{ 
          height: `${(data.initiated / maxValue) * 100}%`,
          minHeight: '8px'
        }}
        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'EXTENSION_INITIATED', bu: buFilter })}
      ></div>
      <div 
        className="flex-1 bg-[#10C1A8] rounded-t cursor-pointer hover:opacity-80 transition-opacity"
        style={{ 
          height: `${(data.confirmed / maxValue) * 100}%`,
          minHeight: '8px'
        }}
        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'CONTRACTS_CONFIRMED', bu: buFilter })}
      ></div>
    </div>
    <div className="text-xs font-medium text-gray-700 mt-2">{data.month}</div>
  </div>
));
                  })()}
                </div>

                
              </div>
            </div>
          </div>

          {/* Extension Performance */}
<div className="bg-white rounded border border-gray-200 p-3">
  <div className="flex items-center space-x-1.5 mb-2 bg-gradient-to-r from-green-100 to-transparent px-2 py-1 rounded-lg w-fit">
    <Target className="w-3.5 h-3.5 text-[#0047bb]" />
    <h3 className="text-xs font-semibold text-[#0047bb]">Extension Performance</h3>
  </div>
  
  <div className="space-y-2">
    <div className="p-2 bg-gray-50 rounded">
      <div className="text-[10px] text-gray-600 mb-0.5">Overall Conversion Rate</div>
      <div className="text-lg font-bold bg-gradient-to-r from-[#0047bb] to-[#0066ff] bg-clip-text text-transparent">
        {metrics.extensionInitiated > 0 ? ((metrics.contractsConfirmed / metrics.extensionInitiated) * 100).toFixed(1) : 0}%
      </div>
    </div>
    
    <div className="p-2 bg-gray-50 rounded">
      <div className="text-[10px] text-gray-600 mb-0.5">Pending Rate</div>
      <div className="text-lg font-bold text-orange-600">
        {metrics.extensionInitiated > 0 ? ((metrics.pendingConfirmations / metrics.extensionInitiated) * 100).toFixed(1) : 0}%
      </div>
    </div>

    <div className="p-2 bg-gray-50 rounded">
      <div className="text-[10px] text-gray-600 mb-0.5">Total Extensions</div>
      <div className="text-lg font-bold text-[#10C1A8]">
        {metrics.contractsConfirmed}
      </div>
    </div>
  </div>
</div>
        </div>
      </div>

      {/* Right Column - BU Distribution */}
      <div className="lg:col-span-3 space-y-3">
        {/* Right: Data Collection Carousel */}
<div className="lg:col-span-3">
  <div className="bg-white rounded border border-gray-200 p-2.5 h-full">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center space-x-1.5 bg-gradient-to-r from-green-100 to-transparent px-2 py-1 rounded-lg">
        <Activity className="w-3.5 h-3.5 text-[#0047bb]" />
        <h3 className="text-xs font-semibold text-[#0047bb]">Data Collection</h3>
      </div>
      <div className="flex items-center space-x-0.5">
        <button
          onClick={() => setCurrentDataSlide((prev) => (prev - 1 + dataSlides.length) % dataSlides.length)}
          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="w-3 h-3 text-gray-600" />
        </button>
        <button
          onClick={() => setCurrentDataSlide((prev) => (prev + 1) % dataSlides.length)}
          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronRight className="w-3 h-3 text-gray-600" />
        </button>
      </div>
    </div>

    <div className="relative overflow-hidden">
      <div 
        className="flex transition-transform duration-500"
        style={{ transform: `translateX(-${currentDataSlide * 100}%)` }}
      >
        {/* Reporting Info Status */}
        <div className="w-full flex-shrink-0 px-0.5">
          {(() => {
            const activeCandidates = filteredDashboardCandidates.filter(c => c?.status === 'Active');
            const totalCount = activeCandidates.length;
            
            const reportingInfoCollected = activeCandidates.filter(c => 
              (c.workEmail && c.workEmail.trim()) ||
              (c.reportingManagerName && c.reportingManagerName.trim()) ||
              (c.reportingManagerEmail && c.reportingManagerEmail.trim())
            ).length;
            
            const reportingInfoPending = totalCount - reportingInfoCollected;
            
            const collectedPercentage = totalCount > 0 ? ((reportingInfoCollected / totalCount) * 100).toFixed(1) : 0;
            const pendingPercentage = totalCount > 0 ? ((reportingInfoPending / totalCount) * 100).toFixed(1) : 0;
            
            if (totalCount === 0) {
              return (
                <div className="text-center py-4">
                  <FileText className="w-8 h-8 text-gray-300 mx-auto mb-1.5" />
                  <p className="text-xs text-gray-500">No active candidates</p>
                </div>
              );
            }
            
            function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
              const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
              return {
                x: centerX + (radius * Math.cos(angleInRadians)),
                y: centerY + (radius * Math.sin(angleInRadians))
              };
            }
            
            const collectedPercentageValue = reportingInfoCollected / totalCount;
            const pendingPercentageValue = reportingInfoPending / totalCount;
            
            const collectedAngle = collectedPercentageValue * 360;
            const pendingAngle = pendingPercentageValue * 360;
            
            let currentAngle = -90;
            
            const collectedStart = polarToCartesian(90, 90, 70, currentAngle + collectedAngle);
            const collectedEnd = polarToCartesian(90, 90, 70, currentAngle);
            const collectedLargeArc = collectedAngle > 180 ? 1 : 0;
            const collectedPath = [
              `M 90 90`,
              `L ${collectedStart.x} ${collectedStart.y}`,
              `A 70 70 0 ${collectedLargeArc} 0 ${collectedEnd.x} ${collectedEnd.y}`,
              'Z'
            ].join(' ');
            
            currentAngle += collectedAngle;
            
            const pendingStart = polarToCartesian(90, 90, 70, currentAngle + pendingAngle);
            const pendingEnd = polarToCartesian(90, 90, 70, currentAngle);
            const pendingLargeArc = pendingAngle > 180 ? 1 : 0;
            const pendingPath = [
              `M 90 90`,
              `L ${pendingStart.x} ${pendingStart.y}`,
              `A 70 70 0 ${pendingLargeArc} 0 ${pendingEnd.x} ${pendingEnd.y}`,
              'Z'
            ].join(' ');
            
            return (
              <div className="space-y-1.5">
                <div className="flex items-center space-x-1 mb-1">
                  <FileText className="w-3 h-3 text-[#0047bb]" />
                  <h4 className="text-[10px] font-semibold text-[#0047bb]">Reporting Info</h4>
                </div>
                <div className="flex items-center justify-center">
                  <svg viewBox="0 0 180 180" className="w-full h-auto max-w-[120px]">
                    <defs>
                      <filter id="shadow-reporting">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                        <feOffset dx="2" dy="4" result="offsetblur"/>
                        <feComponentTransfer>
                          <feFuncA type="linear" slope="0.3"/>
                        </feComponentTransfer>
                        <feMerge>
                          <feMergeNode/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                      <linearGradient id="grad-collected-reporting" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#12D9BE', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#0EA992', stopOpacity: 1 }} />
                      </linearGradient>
                      <linearGradient id="grad-pending-reporting" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#FFA726', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#F57C00', stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                    
                    {reportingInfoCollected > 0 && (
                      <path
                        d={collectedPath.replace('M 90 90', 'M 92 94')}
                        fill="#000"
                        opacity="0.2"
                      />
                    )}
                    {reportingInfoPending > 0 && (
                      <path
                        d={pendingPath.replace('M 90 90', 'M 92 94')}
                        fill="#000"
                        opacity="0.2"
                      />
                    )}
                    
                    {reportingInfoCollected > 0 ? (
                      <path
                        d={collectedPath}
                        fill="url(#grad-collected-reporting)"
                        className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-reporting)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'REPORTING_COLLECTED', bu: buFilter })}
                      />
                    ) : (
                      <circle cx="90" cy="90" r="70" fill="#f5f5f5" />
                    )}
                    {reportingInfoPending > 0 ? (
                      <path
                        d={pendingPath}
                        fill="url(#grad-pending-reporting)"
                        className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-reporting)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'REPORTING_PENDING', bu: buFilter })}
                      />
                    ) : reportingInfoCollected === totalCount ? (
                      <circle cx="90" cy="90" r="70" fill="url(#grad-collected-reporting)" className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-reporting)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'REPORTING_COLLECTED', bu: buFilter })} />
                    ) : null}
                    
                    <circle cx="90" cy="90" r="50" fill="white" filter="url(#shadow-reporting)" />
                    <text x="90" y="85" textAnchor="middle" className="text-2xl font-bold" fill="#0047bb">
                      {totalCount}
                    </text>
                    <text x="90" y="98" textAnchor="middle" className="text-[10px] font-medium" fill="#6b7280">
                      Active
                    </text>
                  </svg>
                </div>
                
                <div className="space-y-1">
                  <div 
                    className="flex items-center justify-between p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'REPORTING_COLLECTED', bu: buFilter })}
                  >
                    <div className="flex items-center space-x-1 flex-1 min-w-0">
                      <div className="w-2 h-2 rounded-full bg-[#10C1A8] flex-shrink-0"></div>
                      <span className="text-[10px] font-medium text-gray-700 truncate">Collected</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-900">{reportingInfoCollected}</span>
                      <span className="text-[9px] text-gray-500">({collectedPercentage}%)</span>
                    </div>
                  </div>
                  
                  <div 
                    className="flex items-center justify-between p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'REPORTING_PENDING', bu: buFilter })}
                  >
                    <div className="flex items-center space-x-1 flex-1 min-w-0">
                      <div className="w-2 h-2 rounded-full bg-[#f59e0b] flex-shrink-0"></div>
                      <span className="text-[10px] font-medium text-gray-700 truncate">Pending</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-900">{reportingInfoPending}</span>
                      <span className="text-[9px] text-gray-500">({pendingPercentage}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Project Details Status */}
        <div className="w-full flex-shrink-0 px-0.5">
          {(() => {
            const activeCandidates = filteredDashboardCandidates.filter(c => c?.status === 'Active');
            const totalCount = activeCandidates.length;
            
            const projectDetailsCollected = activeCandidates.filter(c => 
              (c.project_tech_stack && c.project_tech_stack.trim()) ||
              (c.project_key_technologies && c.project_key_technologies.trim()) ||
              (c.project_business_problem && c.project_business_problem.trim()) ||
              (c.project_scope_objectives && c.project_scope_objectives.trim()) ||
              (c.project_role_responsibility && c.project_role_responsibility.trim()) ||
              (c.project_experience_rating && c.project_experience_rating > 0)
            ).length;
            
            const projectDetailsPending = totalCount - projectDetailsCollected;
            
            const collectedPercentage = totalCount > 0 ? ((projectDetailsCollected / totalCount) * 100).toFixed(1) : 0;
            const pendingPercentage = totalCount > 0 ? ((projectDetailsPending / totalCount) * 100).toFixed(1) : 0;
            
            if (totalCount === 0) {
              return (
                <div className="text-center py-4">
                  <Briefcase className="w-8 h-8 text-gray-300 mx-auto mb-1.5" />
                  <p className="text-xs text-gray-500">No active candidates</p>
                </div>
              );
            }
            
            function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
              const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
              return {
                x: centerX + (radius * Math.cos(angleInRadians)),
                y: centerY + (radius * Math.sin(angleInRadians))
              };
            }
            
            const collectedPercentageValue = projectDetailsCollected / totalCount;
            const pendingPercentageValue = projectDetailsPending / totalCount;
            
            const collectedAngle = collectedPercentageValue * 360;
            const pendingAngle = pendingPercentageValue * 360;
            
            let currentAngle = -90;
            
            const collectedStart = polarToCartesian(90, 90, 70, currentAngle + collectedAngle);
            const collectedEnd = polarToCartesian(90, 90, 70, currentAngle);
            const collectedLargeArc = collectedAngle > 180 ? 1 : 0;
            const collectedPath = [
              `M 90 90`,
              `L ${collectedStart.x} ${collectedStart.y}`,
              `A 70 70 0 ${collectedLargeArc} 0 ${collectedEnd.x} ${collectedEnd.y}`,
              'Z'
            ].join(' ');
            
            currentAngle += collectedAngle;
            
            const pendingStart = polarToCartesian(90, 90, 70, currentAngle + pendingAngle);
            const pendingEnd = polarToCartesian(90, 90, 70, currentAngle);
            const pendingLargeArc = pendingAngle > 180 ? 1 : 0;
            const pendingPath = [
              `M 90 90`,
              `L ${pendingStart.x} ${pendingStart.y}`,
              `A 70 70 0 ${pendingLargeArc} 0 ${pendingEnd.x} ${pendingEnd.y}`,
              'Z'
            ].join(' ');
            
            return (
              <div className="space-y-1.5">
                <div className="flex items-center space-x-1 mb-1">
                  <Briefcase className="w-3 h-3 text-[#0047bb]" />
                  <h4 className="text-[10px] font-semibold text-[#0047bb]">Project Details</h4>
                </div>
                <div className="flex items-center justify-center">
                  <svg viewBox="0 0 180 180" className="w-full h-auto max-w-[120px]">
                    <defs>
                      <filter id="shadow-project">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                        <feOffset dx="2" dy="4" result="offsetblur"/>
                        <feComponentTransfer>
                          <feFuncA type="linear" slope="0.3"/>
                        </feComponentTransfer>
                        <feMerge>
                          <feMergeNode/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                      <linearGradient id="grad-collected-project" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#12D9BE', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#0EA992', stopOpacity: 1 }} />
                      </linearGradient>
                      <linearGradient id="grad-pending-project" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#FFA726', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#F57C00', stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                    
                    {projectDetailsCollected > 0 && (
                      <path
                        d={collectedPath.replace('M 90 90', 'M 92 94')}
                        fill="#000"
                        opacity="0.2"
                      />
                    )}
                    {projectDetailsPending > 0 && (
                      <path
                        d={pendingPath.replace('M 90 90', 'M 92 94')}
                        fill="#000"
                        opacity="0.2"
                      />
                    )}
                    
                    {projectDetailsCollected > 0 ? (
                      <path
                        d={collectedPath}
                        fill="url(#grad-collected-project)"
                        className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-project)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'PROJECT_COLLECTED', bu: buFilter })}
                      />
                    ) : (
                      <circle cx="90" cy="90" r="70" fill="#f5f5f5" />
                    )}
                    {projectDetailsPending > 0 ? (
                      <path
                        d={pendingPath}
                        fill="url(#grad-pending-project)"
                        className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-project)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'PROJECT_PENDING', bu: buFilter })}
                      />
                    ) : projectDetailsCollected === totalCount ? (
                      <circle cx="90" cy="90" r="70" fill="url(#grad-collected-project)" className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-project)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'PROJECT_COLLECTED', bu: buFilter })} />
                    ) : null}
                    
                    <circle cx="90" cy="90" r="50" fill="white" filter="url(#shadow-project)" />
                    <text x="90" y="85" textAnchor="middle" className="text-2xl font-bold" fill="#0047bb">
                      {totalCount}
                    </text>
                    <text x="90" y="98" textAnchor="middle" className="text-[10px] font-medium" fill="#6b7280">
                      Active
                    </text>
                  </svg>
                </div>
                
                <div className="space-y-1">
                  <div 
                    className="flex items-center justify-between p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'PROJECT_COLLECTED', bu: buFilter })}
                  >
                    <div className="flex items-center space-x-1 flex-1 min-w-0">
                      <div className="w-2 h-2 rounded-full bg-[#10C1A8] flex-shrink-0"></div>
                      <span className="text-[10px] font-medium text-gray-700 truncate">Collected</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-900">{projectDetailsCollected}</span>
                      <span className="text-[9px] text-gray-500">({collectedPercentage}%)</span>
                    </div>
                  </div>
                  
                  <div 
                    className="flex items-center justify-between p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'PROJECT_PENDING', bu: buFilter })}
                  >
                    <div className="flex items-center space-x-1 flex-1 min-w-0">
                      <div className="w-2 h-2 rounded-full bg-[#f59e0b] flex-shrink-0"></div>
                      <span className="text-[10px] font-medium text-gray-700 truncate">Pending</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-900">{projectDetailsPending}</span>
                      <span className="text-[9px] text-gray-500">({pendingPercentage}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Client Feedback Status */}
        <div className="w-full flex-shrink-0 px-0.5">
          {(() => {
            const activeCandidates = filteredDashboardCandidates.filter(c => c?.status === 'Active');
            const totalCount = activeCandidates.length;
            
            const clientFeedbackCollected = activeCandidates.filter(c => 
              c.clientRating && 
              c.clientFeedback && 
              c.clientRating !== '0' && 
              c.clientRating !== 0 &&
              typeof c.clientFeedback === 'string' && 
              c.clientFeedback.trim() !== ''
            ).length;
            
            const clientFeedbackPending = totalCount - clientFeedbackCollected;
            
            const collectedPercentage = totalCount > 0 ? ((clientFeedbackCollected / totalCount) * 100).toFixed(1) : 0;
            const pendingPercentage = totalCount > 0 ? ((clientFeedbackPending / totalCount) * 100).toFixed(1) : 0;
            
            if (totalCount === 0) {
              return (
                <div className="text-center py-4">
                  <Star className="w-8 h-8 text-gray-300 mx-auto mb-1.5" />
                  <p className="text-xs text-gray-500">No active candidates</p>
                </div>
              );
            }
            
            function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
              const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
              return {
                x: centerX + (radius * Math.cos(angleInRadians)),
                y: centerY + (radius * Math.sin(angleInRadians))
              };
            }
            
            const collectedPercentageValue = clientFeedbackCollected / totalCount;
            const pendingPercentageValue = clientFeedbackPending / totalCount;
            
            const collectedAngle = collectedPercentageValue * 360;
            const pendingAngle = pendingPercentageValue * 360;
            
            let currentAngle = -90;
            
            const collectedStart = polarToCartesian(90, 90, 70, currentAngle + collectedAngle);
            const collectedEnd = polarToCartesian(90, 90, 70, currentAngle);
            const collectedLargeArc = collectedAngle > 180 ? 1 : 0;
            const collectedPath = [
              `M 90 90`,
              `L ${collectedStart.x} ${collectedStart.y}`,
              `A 70 70 0 ${collectedLargeArc} 0 ${collectedEnd.x} ${collectedEnd.y}`,
              'Z'
            ].join(' ');
            
            currentAngle += collectedAngle;
            
            const pendingStart = polarToCartesian(90, 90, 70, currentAngle + pendingAngle);
            const pendingEnd = polarToCartesian(90, 90, 70, currentAngle);
            const pendingLargeArc = pendingAngle > 180 ? 1 : 0;
            const pendingPath = [
              `M 90 90`,
              `L ${pendingStart.x} ${pendingStart.y}`,
              `A 70 70 0 ${pendingLargeArc} 0 ${pendingEnd.x} ${pendingEnd.y}`,
              'Z'
            ].join(' ');
            
            return (
              <div className="space-y-1.5">
                <div className="flex items-center space-x-1 mb-1">
                  <Star className="w-3 h-3 text-[#0047bb]" />
                  <h4 className="text-[10px] font-semibold text-[#0047bb]">Client Feedback</h4>
                </div>
                <div className="flex items-center justify-center">
                  <svg viewBox="0 0 180 180" className="w-full h-auto max-w-[120px]">
                    <defs>
                      <filter id="shadow-feedback">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                        <feOffset dx="2" dy="4" result="offsetblur"/>
                        <feComponentTransfer>
                          <feFuncA type="linear" slope="0.3"/>
                        </feComponentTransfer>
                        <feMerge>
                          <feMergeNode/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                      <linearGradient id="grad-collected-feedback" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#12D9BE', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#0EA992', stopOpacity: 1 }} />
                      </linearGradient>
                      <linearGradient id="grad-pending-feedback" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#FFA726', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#F57C00', stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                    
                    {clientFeedbackCollected > 0 && (
                      <path
                        d={collectedPath.replace('M 90 90', 'M 92 94')}
                        fill="#000"
                        opacity="0.2"
                      />
                    )}
                    {clientFeedbackPending > 0 && (
                      <path
                        d={pendingPath.replace('M 90 90', 'M 92 94')}
                        fill="#000"
                        opacity="0.2"
                      />
                    )}
                    
                    {clientFeedbackCollected > 0 ? (
                      <path
                        d={collectedPath}
                        fill="url(#grad-collected-feedback)"
                        className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-feedback)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'FEEDBACK_COLLECTED', bu: buFilter })}
                      />
                    ) : (
                      <circle cx="90" cy="90" r="70" fill="#f5f5f5" />
                    )}
                    {clientFeedbackPending > 0 ? (
                      <path
                        d={pendingPath}
                        fill="url(#grad-pending-feedback)"
                        className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-feedback)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'FEEDBACK_PENDING', bu: buFilter })}
                      />
                    ) : clientFeedbackCollected === totalCount ? (
                      <circle cx="90" cy="90" r="70" fill="url(#grad-collected-feedback)" className="hover:opacity-90 transition-opacity cursor-pointer"
                        filter="url(#shadow-feedback)"
                        onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'FEEDBACK_COLLECTED', bu: buFilter })} />
                    ) : null}
                    
                    <circle cx="90" cy="90" r="50" fill="white" filter="url(#shadow-feedback)" />
                    <text x="90" y="85" textAnchor="middle" className="text-2xl font-bold" fill="#0047bb">
                      {totalCount}
                    </text>
                    <text x="90" y="98" textAnchor="middle" className="text-[10px] font-medium" fill="#6b7280">
                      Active
                    </text>
                  </svg>
                </div>
                
                <div className="space-y-1">
                  <div 
                    className="flex items-center justify-between p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'FEEDBACK_COLLECTED', bu: buFilter })}
                  >
                    <div className="flex items-center space-x-1 flex-1 min-w-0">
                      <div className="w-2 h-2 rounded-full bg-[#10C1A8] flex-shrink-0"></div>
                      <span className="text-[10px] font-medium text-gray-700 truncate">Collected</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-900">{clientFeedbackCollected}</span>
                      <span className="text-[9px] text-gray-500">({collectedPercentage}%)</span>
                    </div>
                  </div>
                  
                  <div 
                    className="flex items-center justify-between p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedReasonModal && setSelectedReasonModal({ reason: 'FEEDBACK_PENDING', bu: buFilter })}
                  >
                    <div className="flex items-center space-x-1 flex-1 min-w-0">
                      <div className="w-2 h-2 rounded-full bg-[#f59e0b] flex-shrink-0"></div>
                      <span className="text-[10px] font-medium text-gray-700 truncate">Pending</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-900">{clientFeedbackPending}</span>
                      <span className="text-[9px] text-gray-500">({pendingPercentage}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>

    <div className="flex justify-center space-x-1 mt-1.5">
      {dataSlides.map((_, index) => (
        <button
          key={index}
          onClick={() => setCurrentDataSlide(index)}
          className={`h-1 rounded-full transition-all ${
            index === currentDataSlide 
              ? 'bg-[#003A64] w-2.5' 
              : 'bg-gray-300 w-1'
          }`}
        />
      ))}
    </div>
  </div>
</div>

        {/* Recent Activities & Login Activities */}
<div className="space-y-3">
  {/* Recent Activities */}
  <div className="bg-white rounded border border-gray-200 p-3">
    <div className="flex items-center space-x-1.5 mb-2 bg-gradient-to-r from-purple-100 to-transparent px-2 py-1 rounded-lg w-fit">
      <Activity className="w-3.5 h-3.5 text-[#0047bb]" />
      <h3 className="text-xs font-semibold text-[#0047bb]">Recent Activities</h3>
    </div>
    
    <div className="overflow-y-auto powerbi-scrollbar" style={{ maxHeight: '120px' }}>
      {activitiesLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#0047bb] border-t-transparent"></div>
        </div>
      ) : !activitiesData || !activitiesData.recentActivities || activitiesData.recentActivities.length === 0 ? (
        <div className="text-center py-6">
          <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-xs text-gray-500">No recent activities</p>
        </div>
      ) : (
        <div className="space-y-2">
          {activitiesData.recentActivities.map((activity, idx) => {
            const getActivityIcon = (type) => {
              switch(type) {
                case 'onboarding': return { icon: UserPlus, color: '#10C1A8' };
                case 'extension': return { icon: TrendingUp, color: '#0047bb' };
                case 'feedback': return { icon: Star, color: '#f59e0b' };
                case 'rolloff': return { icon: AlertCircle, color: '#ef4444' };
                case 'update': return { icon: CheckCircle, color: '#10C1A8' };
                default: return { icon: Activity, color: '#6b7280' };
              }
            };
            
            const getTimeAgo = (dateString) => {
              const date = new Date(dateString);
              const now = new Date();
              const diffMs = now - date;
              const diffMins = Math.floor(diffMs / 60000);
              const diffHours = Math.floor(diffMs / 3600000);
              const diffDays = Math.floor(diffMs / 86400000);
              
              if (diffMins < 1) return 'Just now';
              if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
              if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
              return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
            };
            
            const activityConfig = getActivityIcon(activity.type);
            const IconComponent = activityConfig.icon;
            
            return (
              <div 
                key={idx}
                className="flex items-start space-x-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div 
                  className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${activityConfig.color}20` }}
                >
                  <IconComponent className="w-3.5 h-3.5" style={{ color: activityConfig.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-900 font-medium truncate">{activity.message}</p>
                  <p className="text-[10px] text-gray-500">{getTimeAgo(activity.activity_time)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>

  {/* Login Activities */}
  <div className="bg-white rounded border border-gray-200 p-3">
    <div className="flex items-center space-x-1.5 mb-2 bg-gradient-to-r from-blue-100 to-transparent px-2 py-1 rounded-lg w-fit">
      <Eye className="w-3.5 h-3.5 text-[#0047bb]" />
      <h3 className="text-xs font-semibold text-[#0047bb]">Login Activities</h3>
    </div>
    
    {activitiesLoading ? (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#0047bb] border-t-transparent"></div>
      </div>
    ) : !activitiesData || !activitiesData.loginStats ? (
      <div className="text-center py-6">
        <Eye className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-xs text-gray-500">No login data available</p>
      </div>
    ) : (
      <div className="space-y-2">
        <div className="flex items-center justify-between p-2 bg-gradient-to-r from-green-50 to-transparent rounded">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-xs font-medium text-gray-700">Active Users</span>
          </div>
          <span className="text-sm font-bold text-green-600">{activitiesData.loginStats.active_users || 0}</span>
        </div>
        
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
          <div className="flex items-center space-x-2">
            <Clock className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">Today's Logins</span>
          </div>
          <span className="text-sm font-bold text-gray-900">{activitiesData.loginStats.today_logins || 0}</span>
        </div>
        
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
          <div className="flex items-center space-x-2">
            <Users className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">Last 7 Days</span>
          </div>
          <span className="text-sm font-bold text-gray-900">{activitiesData.loginStats.week_logins || 0}</span>
        </div>

        {activitiesData.recentLogins && activitiesData.recentLogins.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-[10px] font-semibold text-gray-600 mb-2">Recent Logins</div>
            <div className="space-y-1.5">
              {activitiesData.recentLogins.slice(0, 3).map((user, idx) => {
                const getTimeAgo = (dateString) => {
                  const date = new Date(dateString);
                  const now = new Date();
                  const diffMs = now - date;
                  const diffMins = Math.floor(diffMs / 60000);
                  
                  if (diffMins < 1) return 'Just now';
                  if (diffMins < 60) return `${diffMins} min ago`;
                  const diffHours = Math.floor(diffMs / 3600000);
                  return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                };
                
                const status = new Date() - new Date(user.login_time) < 1800000 ? 'online' : 'away';
                
                return (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${status === 'online' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                      <span className="text-gray-700 truncate">{user.user_name}</span>
                    </div>
                    <span className="text-gray-500 text-[10px]">{getTimeAgo(user.login_time)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
</div>

      </div>
    </div>
  </div>
</div>
);
};
export default Dashboard;

// Power BI Style Scrollbar
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    .powerbi-scrollbar::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    .powerbi-scrollbar::-webkit-scrollbar-track {
      background: #f5f5f5;
    }
    .powerbi-scrollbar::-webkit-scrollbar-thumb {
      background: #c0c0c0;
      border-radius: 4px;
    }
    .powerbi-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #a0a0a0;
    }
  `;
  if (!document.querySelector('style[data-powerbi-styles]')) {
    styleSheet.setAttribute('data-powerbi-styles', 'true');
    document.head.appendChild(styleSheet);
  }
}